import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, Star, Facebook, Instagram } from "lucide-react";
import { BUSINESS, NAV_LINKS, waLink, SOCIALS } from "@/data/site";

const legal = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Refund Policy", to: "/refund-policy" },
  { label: "Disclaimer", to: "/disclaimer" },
];

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="relative border-t border-border bg-secondary">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <span className="grid place-items-center w-9 h-9 rounded-xl bg-primary text-primary-foreground font-heading font-extrabold text-lg">
                R
              </span>
              <span className="font-heading font-bold text-lg">
                RedKey<span className="text-primary">.</span>
              </span>
            </Link>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-xs">
              {BUSINESS.tagline}. Premium websites, SEO and AI automation for businesses across {BUSINESS.serviceArea}.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-sm">
              <span className="flex items-center gap-1 text-primary">
                <Star className="w-4 h-4 fill-primary" /> {BUSINESS.rating}
              </span>
              <span className="text-muted-foreground">from {BUSINESS.reviews} founders</span>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook"
                aria-label="RedKey Digital on Facebook"
                className="grid place-items-center w-10 h-10 rounded-full border border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
                aria-label="RedKey Digital on Instagram"
                className="grid place-items-center w-10 h-10 rounded-full border border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Explore</h4>
            <ul className="mt-4 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Legal</h4>
            <ul className="mt-4 space-y-3">
              {legal.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Get in touch</h4>
            <ul className="mt-4 space-y-4 text-sm">
              <li>
                <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="footer-phone" className="flex items-start gap-3 text-muted-foreground hover:text-foreground transition-colors">
                  <Phone className="w-4 h-4 mt-0.5 text-primary" /> {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email" className="flex items-start gap-3 text-muted-foreground hover:text-foreground transition-colors">
                  <Mail className="w-4 h-4 mt-0.5 text-primary" /> {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-muted-foreground">
                <MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <span>{BUSINESS.address.line1} {BUSINESS.address.line2} {BUSINESS.address.line3} {BUSINESS.address.line4}</span>
              </li>
            </ul>
            <a
              href={waLink(`Hi ${BUSINESS.name}, I have an enquiry.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-4 py-2.5 transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
          <p>{BUSINESS.hours}</p>
        </div>
      </div>
    </footer>
  );
};
